/**
 * Payload for one Brazilian Portuguese verb.
 *
 * The conjugation is DERIVED at seed time by `scripts/conjugate-pt.ts` and
 * stored already split into stem and ending, so the card back can highlight
 * the ending without the browser knowing any Portuguese morphology.
 *
 * Storing it rather than computing it in the client is deliberate: the engine
 * is build-time tooling whose output is checked against a corpus, and shipping
 * a second copy of the conjugation rules into the bundle would give the app two
 * answers to the same question and no way to notice when they diverge.
 */

/** One form, split where the stem stops and the ending begins. */
export type FormSplit = {
  stem: string;
  ending: string;
  /**
   * The split came from the regular ending table.
   *
   * False for irregular verbs, where the form was compared against its
   * infinitive instead. The card says so rather than implying a pattern that
   * is not there — `s|ou` is not evidence of a rule.
   */
  regular: boolean;
};

export type VerboPayload = {
  conjugation: {
    present: FormSplit[];
    preterite: FormSplit[];
    gerund: FormSplit;
  };
};

/**
 * The row labels, and why they are not just `eu / ele / nós / eles`.
 *
 * She is learning this to talk to Brazilian in-laws. She will address them as
 * `você`, and they will say `a gente vai` roughly every time a bare table would
 * have said `nós vamos`. A paradigm that labels the third-person row `ele`
 * hides the form she will actually use, and one that omits `a gente` hides the
 * form she will actually hear.
 *
 * `a gente` sits in the third-person SINGULAR row, which is the part that looks
 * wrong and is not: it means "we" and takes a singular verb.
 */
export const PERSON_LABELS = ['eu', 'você, ele, ela', 'nós', 'vocês, eles'] as const;

/** The short label, for the row that also has to fit `a gente` beneath it. */
export const PERSON_ALIASES: (string | null)[] = [null, 'a gente', null, null];
